import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { AdminRole } from "@/admin/types/admin";

interface AdminAuthResult {
  isAdmin: boolean;
  adminRole: AdminRole | null;
  adminUserId: string | null;
  isLoading: boolean;
  can: (action: AdminAction) => boolean;
}

type AdminAction =
  | 'verify_artisan'
  | 'reject_artisan'
  | 'suspend_user'
  | 'reactivate_user'
  | 'delete_user'
  | 'moderate_ad'
  | 'manage_reports'
  | 'view_audit_logs'
  | 'manage_admins';

const PERMISSIONS: Record<AdminRole, AdminAction[]> = {
  adminOwner: [
    'verify_artisan',
    'reject_artisan',
    'suspend_user',
    'reactivate_user',
    'delete_user',
    'moderate_ad',
    'manage_reports',
    'view_audit_logs',
    'manage_admins',
  ],
  adminModerator: [
    'verify_artisan',
    'reject_artisan',
    'suspend_user',
    'reactivate_user',
    'moderate_ad',
    'manage_reports',
    'view_audit_logs',
  ],
  adminTech: [
    'view_audit_logs',
  ],
};

export function useAdminAuth(): AdminAuthResult {
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading: queryLoading } = useQuery({
    queryKey: ["admin-auth", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("admin_users")
        .select("id, admin_role")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 0,
  });

  const isAdmin = !!data;
  const adminRole = (data?.admin_role as AdminRole) ?? null;
  const adminUserId = data?.id ?? null;
  const isLoading = authLoading || queryLoading;

  const can = (action: AdminAction): boolean => {
    if (!adminRole) return false;
    return PERMISSIONS[adminRole]?.includes(action) ?? false;
  };

  return { isAdmin, adminRole, adminUserId, isLoading, can };
}
